import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { useUserAgents } from "@/hooks/agent/useAgents";
import type { Agent } from "@/types/agent/agent";
import {
  useCreateInboxView,
  useInboxView,
  useInboxViews,
  useSuggestViews,
  useUpdateInboxView,
  useUpgradeInboxViews,
} from "@/hooks/inboxView/useInboxViews";
import { HeroPrompt } from "./components/HeroPrompt";
import { SuggestionCard } from "./components/SuggestionCard";
import { ThinkingPanel } from "./components/ThinkingPanel";
import { ViewCard } from "./components/ViewCard";
import { ViewDetail } from "./ViewDetail";
import type { InboxView, InboxViewSuggestion, InboxViewTimeRange } from "@/types/inboxView/inboxView";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Props {
  embedded?: boolean;
}

const DEFAULT_TIME_RANGE: InboxViewTimeRange = { kind: "rolling", value: "last_30_days" };
const VIEW_SEARCH_THRESHOLD = 5;

const ViewsPage = ({ embedded: _embedded }: Props) => {
  const [openViewId, setOpenViewId] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string[] | null>(null);
  const [timeRange, setTimeRange] = useState<InboxViewTimeRange>(DEFAULT_TIME_RANGE);
  const [suggestions, setSuggestions] = useState<InboxViewSuggestion[] | null>(null);
  const [suggestionReason, setSuggestionReason] = useState<string | null>(null);
  const [pickingSuggestion, setPickingSuggestion] = useState<string | null>(null);
  const [viewSearch, setViewSearch] = useState("");

  const viewsQuery = useInboxViews();
  const suggest = useSuggestViews();
  const create = useCreateInboxView();
  const update = useUpdateInboxView();
  const { data: agents = [] } = useUserAgents(100);

  const views = viewsQuery.data?.views ?? [];

  const showViewSearch = views.length > VIEW_SEARCH_THRESHOLD;
  const viewQuery = showViewSearch ? viewSearch.trim().toLowerCase() : "";
  const visibleViews = viewQuery
    ? views.filter((v) => `${v.name ?? ""}\n${v.prompt}`.toLowerCase().includes(viewQuery))
    : views;

  // Views made before screening rules existed. They can be upgraded together
  // from here, or one at a time by opening them.
  const upgrade = useUpgradeInboxViews();
  const [upgradingIds, setUpgradingIds] = useState<string[]>([]);
  const outdatedViews = views.filter((v) => !v.screened_at);
  const waitingViews = outdatedViews.filter((v) => !upgradingIds.includes(v.id));
  const stillUpgrading = outdatedViews.filter((v) => upgradingIds.includes(v.id)).length;

  const upgradeStartedAtRef = useRef(0);

  const startUpgrade = () => {
    const ids = waitingViews.map((v) => v.id);
    upgradeStartedAtRef.current = Date.now();
    setUpgradingIds((prev) => [...prev, ...ids]);
    upgrade.mutate(ids, {
      onSuccess: ({ failed }) => {
        if (failed.length === 0) return;
        setUpgradingIds((prev) => prev.filter((id) => !failed.includes(id)));
        toast.error(`${failed.length} ${failed.length === 1 ? "view" : "views"} could not be started. Try again.`);
      },
      onError: () => setUpgradingIds((prev) => prev.filter((id) => !ids.includes(id))),
    });
  };

  // A view counts as upgraded once the list reports its rules as applied, which
  // happens shortly after its run starts — so re-check the list until then.
  const refetchViews = viewsQuery.refetch;

  // A view whose upgrade run has ended but which still is not upgraded could
  // not have its conditions worked out. Hand it back to the button instead of
  // showing "Upgrading..." forever.
  const stalledIds = outdatedViews
    .filter((v) => {
      const finishedAt = v.last_run?.completed_at ? new Date(v.last_run.completed_at).getTime() : 0;
      return upgradingIds.includes(v.id) && finishedAt > upgradeStartedAtRef.current;
    })
    .map((v) => v.id);
  const stalledKey = stalledIds.join(",");
  useEffect(() => {
    if (!stalledKey) return;
    const stalled = stalledKey.split(",");
    setUpgradingIds((prev) => prev.filter((id) => !stalled.includes(id)));
    toast.error(
      `${stalled.length} ${stalled.length === 1 ? "view" : "views"} could not be upgraded and ${stalled.length === 1 ? "was" : "were"} left unchanged. Try again.`,
    );
  }, [stalledKey]);

  useEffect(() => {
    if (stillUpgrading === 0) {
      if (upgradingIds.length > 0) {
        setUpgradingIds([]);
        toast.success("Your views now use smarter matching. Open one to see what changed.");
      }
      return;
    }
    const timer = window.setInterval(() => refetchViews(), 4000);
    return () => window.clearInterval(timer);
  }, [stillUpgrading, upgradingIds.length, refetchViews]);

  // Suggestions are opt-in: the customer clicks "Get ideas" and we fire one
  // request scoped to the current Genie + time-range selection. Changing the
  // scope clears any stale suggestions so the screen never shows ideas that
  // are for a different filter than what's currently selected.
  const fetchSuggestions = () => {
    if (suggest.isPending) return;
    setSuggestionReason(null);
    suggest.mutate(
      { agentFilter, timeRange },
      {
        onSuccess: (res) => {
          setSuggestions(res.suggestions);
          setSuggestionReason(res.reason ?? null);
        },
        onError: () => {
          setSuggestions([]);
          setSuggestionReason("error");
        },
      },
    );
  };

  const handleAgentFilterChange = (next: string[] | null) => {
    setAgentFilter(next);
    setSuggestions(null);
    setSuggestionReason(null);
  };

  const handleTimeRangeChange = (next: InboxViewTimeRange) => {
    setTimeRange(next);
    setSuggestions(null);
    setSuggestionReason(null);
  };

  const handleHeroSubmit = (prompt: string) => {
    create.mutate(
      { prompt, agentFilter, timeRange },
      {
        onSuccess: ({ view }) => setOpenViewId(view.id),
      },
    );
  };

  const handlePickSuggestion = (s: InboxViewSuggestion) => {
    setPickingSuggestion(s.title);
    create.mutate(
      { prompt: s.prompt, agentFilter, timeRange },
      {
        onSuccess: ({ view }) => {
          setPickingSuggestion(null);
          setOpenViewId(view.id);
        },
        onError: () => setPickingSuggestion(null),
      },
    );
  };

  if (openViewId) {
    return <ViewDetail viewId={openViewId} onBack={() => setOpenViewId(null)} />;
  }

  return (
    <div>
      <ViewsExplainer />

      <HeroPrompt
        agentFilter={agentFilter}
        onAgentFilterChange={handleAgentFilterChange}
        timeRange={timeRange}
        onTimeRangeChange={handleTimeRangeChange}
        onSubmit={handleHeroSubmit}
        disabled={create.isPending}
        suggestions={suggestions}
        suggestionsLoading={suggest.isPending}
        suggestionReason={suggestionReason}
        onFetchSuggestions={fetchSuggestions}
        onPickSuggestion={handlePickSuggestion}
        pickingTitle={pickingSuggestion}
        showSuggestionChips={views.length > 0}
      />

      {/* Creation-in-flight panel (no view id yet — fake the status until the view lands) */}
      {create.isPending && !create.data && (
        <div className="mt-5">
          <ThinkingPanel status="designing" message="Reading your transcripts to design the columns..." />
        </div>
      )}

      {/* Cold-start suggestions */}
      {views.length === 0 && !viewsQuery.isLoading && (
        <ColdStartPanel
          suggestions={suggestions}
          reason={suggestionReason}
          loading={suggest.isPending}
          pickingTitle={pickingSuggestion}
          onPick={handlePickSuggestion}
          onFetch={fetchSuggestions}
        />
      )}

      {outdatedViews.length > 0 && (
        <div className="mt-7 rounded-2xl p-[2px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" role="status">
          <div className="flex flex-col gap-3 rounded-[14px] bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#222B45]">
                <GenieIcon name="sparkles" size={16} color="#4E9CFF" label="" className="shrink-0" />
                {waitingViews.length > 0
                  ? `${waitingViews.length} of your views can be upgraded to smarter matching`
                  : `Upgrading ${stillUpgrading} ${stillUpgrading === 1 ? "view" : "views"}...`}
              </div>
              <p className="mt-1 text-xs text-[#64748B]">
                Your Genies now check every conversation against what you asked for, instead of adding any conversation
                that mentions the topic. Rows that don&rsquo;t meet a view&rsquo;s conditions are removed; the rest stay
                as they are. It happens once per view and takes a minute or two.
              </p>
              {waitingViews.length > 0 && (
                <p className="mt-1 text-xs font-medium text-[#222B45]">
                  Until you upgrade a view it keeps working as it does today, including its scheduled report and
                  instant alert. Nothing in it changes on its own.
                </p>
              )}
            </div>
            {waitingViews.length > 0 ? (
              <Button
                onClick={startUpgrade}
                disabled={upgrade.isPending}
                className="shrink-0 bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30"
              >
                {upgrade.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Upgrade {waitingViews.length === 1 ? "this view" : `all ${waitingViews.length} views`}
              </Button>
            ) : (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-genie-primary" />
            )}
          </div>
        </div>
      )}

      {/* Saved views list */}
      {views.length > 0 && (
        <div className="mt-7">
          <div className="flex flex-col gap-3 mb-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Your views</h2>
              <p className="text-xs text-[#64748B] mt-0.5">
                {viewQuery ? `${visibleViews.length} of ${views.length}` : views.length} saved. Click any view to expand
                and refine.
              </p>
            </div>
            {showViewSearch && (
              <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] w-full sm:w-64 shrink-0">
                <div className="rounded-[10px] bg-white">
                  <SearchInput
                    value={viewSearch}
                    onChange={setViewSearch}
                    placeholder="Search views..."
                    inputClassName="h-11 sm:h-10 text-base sm:text-sm rounded-[10px] border-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </div>
            )}
          </div>
          {viewQuery && visibleViews.length === 0 && (
            <div className="rounded-2xl border border-[#E5EAF0] bg-white px-4 py-6 text-center">
              <p className="text-sm text-[#64748B]">No views match &ldquo;{viewSearch.trim()}&rdquo;.</p>
              <button
                type="button"
                onClick={() => setViewSearch("")}
                className="mt-2 min-h-[44px] px-3 text-sm font-semibold text-genie-primary hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
          <div className="space-y-3">
            {visibleViews.map((v) => (
              <ViewCardWrapper
                key={v.id}
                view={v}
                agents={agents}
                onClick={() => setOpenViewId(v.id)}
                onTogglePin={() => update.mutate({ viewId: v.id, pinned: !v.pinned })}
                pinPending={update.isPending && update.variables?.viewId === v.id}
              />
            ))}
          </div>
        </div>
      )}

      {viewsQuery.isLoading && (
        <div className="mt-8 flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
        </div>
      )}
    </div>
  );
};

// One-line explainer that lives above the prompt. Sets expectations so a new
// customer doesn't land on the Views tab going "what the hell is this?".
const ViewsExplainer = () => {
  return (
    <div className="mb-5 flex items-start gap-3 bg-white border border-[#E5EAF0] rounded-2xl px-4 py-3">
      <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10 flex items-center justify-center shrink-0">
        <GenieIcon
          name="document"
          size={20}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="Views"
        />
      </div>
      <div className="text-sm leading-snug">
        <span className="font-semibold text-[#222B45]">Views turn conversations into spreadsheets.</span>
        <span className="text-[#64748B]"> Describe what you want — your Genies read the transcripts and fill in the columns. Each Refresh adds new conversations to your view; your existing rows stay put.</span>
      </div>
    </div>
  );
};

interface ViewCardWrapperProps {
  view: InboxView;
  agents: Agent[];
  onClick: () => void;
  onTogglePin: () => void;
  pinPending: boolean;
}

// Wrapper that fetches a couple of preview rows for the card display.
const ViewCardWrapper = ({ view, agents, onClick, onTogglePin, pinPending }: ViewCardWrapperProps) => {
  const { data } = useInboxView(view.id);
  return (
    <ViewCard
      view={view}
      agents={agents}
      previewRows={data?.rows?.slice(0, 3) ?? []}
      totalRows={data?.rows?.length}
      onClick={onClick}
      onTogglePin={onTogglePin}
      pinPending={pinPending}
    />
  );
};

interface ColdStartProps {
  suggestions: InboxViewSuggestion[] | null;
  reason: string | null;
  loading: boolean;
  pickingTitle: string | null;
  onPick: (s: InboxViewSuggestion) => void;
  onFetch: () => void;
}

const ColdStartPanel = ({ suggestions, reason, loading, pickingTitle, onPick, onFetch }: ColdStartProps) => {
  if (reason === "no_conversations") {
    return (
      <div className="mt-7 bg-white border border-[#E5EAF0] rounded-2xl px-6 py-10 text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10 mb-3">
          <GenieIcon
            name="chat"
            size={28}
            color="#4E9CFF"
            secondaryColor="#FF4DB8"
            gradientEnabled
            label="No conversations yet"
          />
        </div>
        <h3 className="text-lg font-bold text-[#222B45]">No conversations yet</h3>
        <p className="text-sm text-[#64748B] mt-1 max-w-md mx-auto">
          Once your Genies start having conversations, Help Genie will suggest some views you'll probably want — or you can describe your own up there.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-7 flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-genie-primary mr-2" />
        <span className="text-sm text-[#64748B]">Reading your conversations to suggest some views...</span>
      </div>
    );
  }

  // No suggestions yet — show a CTA so the customer triggers the fetch.
  // Auto-firing on mount left stale "wrong-genie" results on screen during
  // filter changes; explicit fetch is cheaper and more predictable.
  if (suggestions === null) {
    return (
      <div className="mt-7 bg-white border border-[#E5EAF0] rounded-2xl px-6 py-10 text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10 mb-3">
          <GenieIcon
            name="sparkles"
            size={28}
            color="#4E9CFF"
            secondaryColor="#FF4DB8"
            gradientEnabled
            label="Suggest views"
          />
        </div>
        <h3 className="text-lg font-bold text-[#222B45]">Want some ideas?</h3>
        <p className="text-sm text-[#64748B] mt-1 max-w-md mx-auto mb-4">
          Help Genie will read your selected Genies' recent conversations and propose three views you could save.
        </p>
        <button
          onClick={onFetch}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        >
          <GenieIcon name="sparkles" size={14} color="#ffffff" label="" />
          Suggest views for me
        </button>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <div className="mt-7 bg-white border border-[#E5EAF0] rounded-2xl px-6 py-8 text-center">
        <p className="text-sm text-[#64748B] mb-3">Couldn't suggest views just now. Try describing one above, or try again.</p>
        <button
          onClick={onFetch}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-genie-primary hover:opacity-80"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="mt-7">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#64748B]">Suggested for you</h2>
          <p className="text-xs text-[#64748B] mt-0.5">Based on the conversations your Genies have had recently.</p>
        </div>
        <button
          onClick={onFetch}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-genie-primary hover:opacity-80"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Regenerate
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {suggestions.map((s) => (
          <SuggestionCard
            key={s.title}
            suggestion={s}
            onPick={() => onPick(s)}
            pending={pickingTitle === s.title}
          />
        ))}
      </div>
    </div>
  );
};

export default ViewsPage;
